import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import {
  followingOptions,
  industryTypeOptions,
} from '@nxsys/constants/AppConst';
import { useUpdateCompanyById } from '@nxsys/hooks/api/company';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { UpdateCompanyByIdParams } from '@nxsys/types/api/company';
import { Option } from '@nxsys/types/models/Common';
import { ICompany } from '@nxsys/types/models/company';
import { showMessage } from '@nxsys/utils/message';
import { Form } from 'antd';
import React, { useEffect, useState } from 'react';

type IndustryTypeModalProps = {
  isOpen: boolean;
  toggle: () => void;
  data?: ICompany;
};

export type IndustryTypeValues = {
  industries: Option<string>[] | null;
  following: Option<string> | null;
};

const IndustryTypeModal: React.FC<IndustryTypeModalProps> = ({
  isOpen,
  toggle,
  data,
}: IndustryTypeModalProps) => {
  const { messages } = useTypedIntl();

  const [needReinitializeForm, setNeedReinitializeForm] = useState(true);
  const [form] = Form.useForm<IndustryTypeValues>();

  useEffect(() => {
    if (data?.empSetting && needReinitializeForm) {
      form.setFieldsValue({
        industries: industryTypeOptions.filter((industry) =>
          data.empSetting.industries?.includes(industry.value),
        ),
        following: followingOptions.find(
          (option) => option.value === data.empSetting.following,
        ),
      });
      setNeedReinitializeForm(false);
    }
  }, [data?.empSetting]);

  const { run: updateCompany, loading: isUpdatingCompany } =
    useUpdateCompanyById();

  const onFinish = async (values: IndustryTypeValues) => {
    if (!data) return;
    const params: UpdateCompanyByIdParams = {
      empSetting: {
        ...data.empSetting,
        industries: values.industries?.map((industry) => industry.value) || [],
        following: values.following?.value || null,
      },
    };
    const res = await updateCompany(data.id, params);
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['common.updateSuccess'],
      });
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={messages['common.selectIndustryType']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.save'],
          buttonType: 'primary',
          onClick: () => form.submit(),
          loading: isUpdatingCompany,
          disabled: isUpdatingCompany,
        },
      ]}
      minHeight="200px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        <div className="text-[14px] font-bold mb-[16px]">
          {messages['common.selectIndustryTypeHelperText']}
        </div>
        <Form.Item label={messages['common.industries']} name="industries">
          <AppSelect
            options={industryTypeOptions}
            mode="multiple"
            placeholder={messages['form.select']}
          />
        </Form.Item>
        <Form.Item label={messages['common.following']} name="following">
          <AppSelect
            options={followingOptions}
            optionRender={(option: any) => (
              <div>
                <div>{option.label}</div>
                <small className="text-muted">{option.data?.desc}</small>
              </div>
            )}
            placeholder={messages['form.select']}
          />
        </Form.Item>
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default IndustryTypeModal;
